/**
 * Training System Types
 *
 * TypeScript interfaces for the Interactive Training System.
 * Data is stored in Firestore at users/{userId}/trainingProfile (single document).
 */

import { Timestamp } from 'firebase/firestore';
import type { LucideIcon } from 'lucide-react';

// ============================================================================
// FEATURE PROGRESS
// ============================================================================

/**
 * All trackable features in the platform
 */
export type TrainingFeatureId =
  | 'documents'
  | 'cim'
  | 'sector-research'
  | 'transaction-search'
  | 'ic-committee'
  | 'ic-memo'
  | 'presentations'
  | 'infographics'
  | 'chat'
  | 'legal'
  | 'competitive-intel'
  | 'customer-cube'
  | 'questions'
  | 'model-generator'
  | 'pipeline';

export type FeatureProgressStatus = 'not_started' | 'started' | 'intermediate' | 'proficient';

/**
 * Progress tracking for a single feature
 */
export interface FeatureProgress {
  status: FeatureProgressStatus;
  percentage: number; // 0-100
  firstUsedAt: Timestamp | null;
  lastUsedAt: Timestamp | null;
  usageCount: number;
  /** Milestones completed for this feature */
  milestones: string[];
}

// ============================================================================
// TRAINING LEVELS & XP
// ============================================================================

export interface TrainingLevel {
  level: number;
  name: string;
  minXp: number;
  maxXp: number;
}

/**
 * XP level definitions - progress through platform mastery
 */
export const TRAINING_LEVELS: TrainingLevel[] = [
  { level: 1, name: 'New Analyst', minXp: 0, maxXp: 100 },
  { level: 2, name: 'Junior Associate', minXp: 100, maxXp: 300 },
  { level: 3, name: 'Associate', minXp: 300, maxXp: 600 },
  { level: 4, name: 'Senior Associate', minXp: 600, maxXp: 1000 },
  { level: 5, name: 'VP', minXp: 1000, maxXp: 1500 },
  { level: 6, name: 'Director', minXp: 1500, maxXp: 2200 },
  { level: 7, name: 'Managing Director', minXp: 2200, maxXp: 3000 },
  { level: 8, name: 'Partner', minXp: 3000, maxXp: 4000 },
  { level: 9, name: 'Senior Partner', minXp: 4000, maxXp: 5500 },
  { level: 10, name: 'Platform Master', minXp: 5500, maxXp: Infinity },
];

/**
 * Get level from XP amount
 */
export function getLevelFromXp(xp: number): TrainingLevel {
  for (let i = TRAINING_LEVELS.length - 1; i >= 0; i--) {
    if (xp >= TRAINING_LEVELS[i].minXp) {
      return TRAINING_LEVELS[i];
    }
  }
  return TRAINING_LEVELS[0];
}

/**
 * Get progress within current level (0-100%)
 */
export function getLevelProgress(xp: number): number {
  const level = getLevelFromXp(xp);
  if (level.maxXp === Infinity) return 100;
  const range = level.maxXp - level.minXp;
  const progress = xp - level.minXp;
  return Math.min(100, Math.round((progress / range) * 100));
}

// ============================================================================
// TRAINING PROFILE (Firestore Document)
// ============================================================================

/**
 * Main training profile stored at users/{userId}/trainingProfile/main
 */
export interface TrainingProfile {
  totalXp: number;
  currentStreakDays: number;
  longestStreakDays: number;
  lastActivityDate: Timestamp | null;
  onboardingCompleted: boolean;
  role: string;
  featureProgress: Record<TrainingFeatureId, FeatureProgress>;
  achievements: string[]; // achievement IDs
  dismissedTriggers: string[]; // trigger IDs the user has dismissed
  updatedAt: Timestamp;
  createdAt: Timestamp;
}

/**
 * Default empty feature progress
 */
export function createDefaultFeatureProgress(): FeatureProgress {
  return {
    status: 'not_started',
    percentage: 0,
    firstUsedAt: null,
    lastUsedAt: null,
    usageCount: 0,
    milestones: [],
  };
}

/**
 * Create a default training profile for new users
 */
export function createDefaultTrainingProfile(): Omit<TrainingProfile, 'updatedAt' | 'createdAt'> {
  const features: Partial<Record<TrainingFeatureId, FeatureProgress>> = {};
  const allFeatures: TrainingFeatureId[] = [
    'documents', 'cim', 'sector-research', 'transaction-search',
    'ic-committee', 'ic-memo', 'presentations', 'infographics',
    'chat', 'legal', 'competitive-intel', 'customer-cube',
    'questions', 'model-generator', 'pipeline',
  ];
  for (const f of allFeatures) {
    features[f] = createDefaultFeatureProgress();
  }

  return {
    totalXp: 0,
    currentStreakDays: 0,
    longestStreakDays: 0,
    lastActivityDate: null,
    onboardingCompleted: false,
    role: '',
    featureProgress: features as Record<TrainingFeatureId, FeatureProgress>,
    achievements: [],
    dismissedTriggers: [],
  };
}

// ============================================================================
// ACHIEVEMENTS
// ============================================================================

export type AchievementCategory = 'exploration' | 'mastery' | 'streak' | 'milestone';

export interface AchievementDefinition {
  id: string;
  name: string;
  description: string;
  category: AchievementCategory;
  icon: string; // emoji
  xpReward: number;
  /** Criteria function - checked against training profile */
  criteria: (profile: TrainingProfile) => boolean;
}

// ============================================================================
// CONTEXTUAL TRIGGERS
// ============================================================================

export type TriggerType = 'tooltip' | 'spotlight' | 'nudge';
export type TriggerCondition = 'feature_not_used' | 'feature_used_once' | 'milestone_available' | 'streak_reminder';

export interface ContextualTriggerDefinition {
  id: string;
  type: TriggerType;
  /** Which page/tab this trigger appears on */
  location: string;
  /** Condition that must be true to show */
  condition: TriggerCondition;
  /** Feature this trigger relates to */
  featureId?: TrainingFeatureId;
  title: string;
  message: string;
  /** Optional action button label */
  actionLabel?: string;
  /** Optional action - tab to navigate to */
  actionTab?: string;
  /** Priority (higher = more important, shown first) */
  priority: number;
  /** Max times to show this trigger (0 = unlimited until dismissed) */
  maxShows: number;
}

// ============================================================================
// CERTIFICATION PATHS
// ============================================================================

export interface CertificationStep {
  id: string;
  label: string;
  featureId: TrainingFeatureId;
  /** Milestone that must be completed */
  requiredMilestone: string;
}

export interface CertificationPath {
  id: string;
  name: string;
  description: string;
  icon: string; // emoji
  steps: CertificationStep[];
  xpReward: number;
}

// ============================================================================
// FEATURE METADATA (for display in Training Hub)
// ============================================================================

export interface FeatureMetadata {
  id: TrainingFeatureId;
  name: string;
  description: string;
  icon: LucideIcon;
  tab: string; // ProjectDetail tab name
  /** Milestones that can be earned for this feature */
  milestones: { id: string; label: string; xp: number }[];
}

// ============================================================================
// XP EVENT TYPES
// ============================================================================

export type XpEventType =
  | 'feature_first_use'
  | 'feature_use'
  | 'milestone_complete'
  | 'achievement_unlock'
  | 'streak_bonus'
  | 'certification_complete';

export interface XpEvent {
  type: XpEventType;
  featureId?: TrainingFeatureId;
  amount: number;
  description: string;
}
